/**
 * Floating attribute table — a desktop-style window over the map.
 *
 * ARCHITECTURE: this renders as a SIBLING of MapView inside the map container,
 * never inside the TOC. Two reasons:
 *   1. The TOC is a glass surface, and `backdrop-filter` makes an ancestor the
 *      containing block for `position: fixed` descendants — a fixed window
 *      nested in the TOC gets clipped into the TOC's box instead of floating.
 *   2. The map owns the window's lifetime, so the TOC only has to say
 *      "open the table for this layer".
 *
 * INTERACTION: there is deliberately NO full-screen backdrop. The map stays
 * pannable, zoomable, and clickable everywhere the window does not physically
 * cover. Wheel events over the table scroll the table only — the window is
 * outside the map's own viewport element, so the map never sees them, and
 * `overscroll-behavior: contain` stops scroll chaining.
 *
 * The data-loading logic is unchanged from the original in-TOC table: same WFS
 * request for GeoServer, same `/query` for ArcGIS, same CSV export.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownAZ,
  ArrowUpAZ,
  Calculator,
  Download,
  ListFilter,
  Loader2,
  Maximize2,
  MoreVertical,
  Minus,
  PanelBottom,
  PanelRight,
  Pencil,
  Plus,
  Search,
  Table2,
  X,
} from 'lucide-react';
import type { Layer } from '../../types';
import { usePermissions } from '../../auth/AuthContext';
import { arcgisServiceUrlFor, getArcgisBaseUrl, withArcgisToken } from '../../utils/arcgis';
import { getLayerDisplayName } from '../../layers/capabilities';
import { filteredLocalFeatures } from '../../layers/localFilter';
import { featureKey } from '../../utils/featureKey';
import {
  addFieldToLayer,
  readEditInfo,
  saveFeatureAttributesUpdate,
  LOCAL_ID_FIELD,
  type EditableField,
  type NewFieldDef,
  type ServiceEditInfo,
} from '../../layers/featureEditing';
import {
  ATTRIBUTE_OPERATORS,
  clauseIsComplete,
  describeClauses,
  distinctValues,
  matchesClauses,
  operatorNeedsValue,
  type AttributeClause,
  type AttributeOperator,
} from './attributeQuery';
import LayerOptionsMenu, { type LayerMenuItem } from './LayerOptionsMenu';
import FieldCalculatorDialog from './FieldCalculatorDialog';

export type AttributeFeature = Record<string, unknown> & {
  id: string;
  properties: Record<string, unknown>;
  geometry?: GeoJSON.Geometry;
  /** Shared identity, so a query result and a table row can be matched. */
  key?: string;
};

const MIN_WIDTH = 450;
const MIN_HEIGHT = 250;
/** How much of the window must stay inside the map, so it can always be grabbed. */
const KEEP_VISIBLE = 120;

interface Geometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Props {
  layer: Layer;
  geoServerUrl: string;
  arcgisUrl?: string;
  onClose: () => void;
  onSelectFeature?: (feature: AttributeFeature | null) => void;
  onZoomToSelection?: (features: AttributeFeature[]) => void;
  /**
   * The whole ticked set, whenever it changes.
   *
   * Separate from `onSelectFeature`, which reports the ONE row just clicked
   * for the pop-up. The map needs the full set to highlight it, and deriving
   * that from a stream of single rows would drift the moment "select all" or a
   * search-scoped tick changed several at once.
   */
  onSelectionChange?: (features: AttributeFeature[]) => void;
  /**
   * Rows to arrive ticked, as `featureKey` values.
   *
   * Keys rather than row numbers: this window issues its OWN request, so the
   * Query tool's fourth result is not this table's fourth row unless both
   * happened to fetch the same page in the same order.
   */
  preselectedKeys?: string[];
  /**
   * Writes an attribute change (or a newly added field) back to `layers`
   * state — the same shape EditPanels.tsx's own `onLayerChange`/
   * `changeTemporaryLayer` already use. Omit it and the table's inline
   * editing/add-field controls simply don't appear, the same way `sldOnly`
   * hides symbology controls that would not apply.
   */
  onLayerChange?: (layerId: string, changes: Partial<Layer>) => void;
  /** False in the viewer, where a normal user may not restructure the map —
   *  a shared layer stays editable there regardless (see `layer.sharedLayerId`). */
  canManageLayers?: boolean;
  /** Needed to write an edit through to a SHARED layer's server copy. */
  moduleId?: string;
  /**
   * Free-floating (default, draggable/resizable window) vs docked to one
   * side of the map — like ArcGIS Pro's dockable panes: the panel takes a
   * genuine, fixed half of the map area rather than overlaying it, so the
   * parent resizes the actual map container and the map itself reflows into
   * the remaining space (see App.tsx, where this state actually lives —
   * it has to, since docking changes the PARENT's layout, not just this
   * component's own position).
   */
  dock?: 'float' | 'right' | 'bottom';
  onDockChange?: (dock: 'float' | 'right' | 'bottom') => void;
  /** Docked size, as a fraction of the map area (0.5 = half) — dragging the
   *  divider on the map-facing edge reports a new value here. Defaults to
   *  0.5 so an old caller that never passes it still gets today's behaviour. */
  dockSize?: number;
  onDockSizeChange?: (size: number) => void;
}

const MIN_DOCK_FRACTION = 0.2;
const MAX_DOCK_FRACTION = 0.8;

/**
 * Builds the WFS GetFeature URL for a GeoServer layer.
 *
 * `cqlFilter` is the layer's own filter — the same expression the map's WMS
 * request carries as CQL_FILTER. Without it the table answered a different
 * question from the map: filter a pipeline layer to DIA 125 and the map drew
 * 125s while the table still listed everything.
 */
function getWfsUrl(geoServerUrl: string, layerName: string, cqlFilter?: string) {
  try {
    const url = new URL(geoServerUrl.trim());
    const path = url.pathname.replace(/\/+$/, '');
    url.pathname = /\/(wms|wfs)$/i.test(path)
      ? path.replace(/\/(wms|wfs)$/i, '/wfs')
      : `${path}/wfs`;
    url.search = '';
    url.searchParams.set('service', 'WFS');
    url.searchParams.set('request', 'GetFeature');
    url.searchParams.set('version', '2.0.0');
    url.searchParams.set('typeNames', layerName);
    url.searchParams.set('outputFormat', 'application/json');
    /* Without this, a layer whose STORED (native) CRS is not WGS84 comes back
       in that native CRS instead — geoJSON would then read those coordinates
       as if they were already lon/lat degrees, placing the row's geometry
       nowhere near the real feature. The attributes are unaffected (they are
       plain text/numbers), so the table itself always looked fine; only
       anything using the GEOMETRY — the map's "selected row" highlight here —
       silently failed for exactly such a layer. See featureSearchService.ts,
       the one other WFS GetFeature caller that already set this. */
    url.searchParams.set('srsName', 'EPSG:4326');
    /* 2,000, matching the ArcGIS branch below. It was 100, and that mismatch
       was a real bug rather than a tuning choice: the Select tool reads up to
       2,000 features, so a box could catch feature 900 and the table — holding
       only the first 100 — would tick nothing and report "Nothing selected"
       while the tool said 23. Both sides now read the same window. */
    url.searchParams.set('count', '2000');
    if (cqlFilter?.trim()) url.searchParams.set('cql_filter', cqlFilter.trim());
    return url.toString();
  } catch {
    return '';
  }
}

/** One field's input in the edit-attributes popup — a coded-value domain
 *  becomes a select, a boolean becomes True/False, everything else a plain
 *  text/number input. Mirrors EditPanels.tsx's own inline field rendering
 *  so a value edited from either place is entered the same way. */
function EditFieldInput({
  field,
  value,
  onChange,
}: {
  field: EditableField;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const inputClass = 'w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12.5px]';
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-medium text-slate-600">{field.alias}</span>
      {field.choices ? (
        <select
          value={String(value ?? '')}
          onChange={(event) => onChange(event.target.value)}
          className={`${inputClass} bg-white`}
        >
          <option value="">—</option>
          {field.choices.map((choice) => (
            <option key={String(choice.code)} value={String(choice.code)}>
              {choice.label}
            </option>
          ))}
        </select>
      ) : field.type === 'boolean' ? (
        <select
          value={String(value ?? '')}
          onChange={(event) => onChange(event.target.value === 'true')}
          className={`${inputClass} bg-white`}
        >
          <option value="">—</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      ) : (
        <input
          type={field.type === 'number' ? 'number' : 'text'}
          value={String(value ?? '')}
          onChange={(event) => onChange(event.target.value)}
          className={inputClass}
        />
      )}
    </label>
  );
}

export default function FloatingAttributeTable({
  layer,
  geoServerUrl,
  arcgisUrl = '',
  onClose,
  onSelectFeature,
  onZoomToSelection,
  onSelectionChange,
  preselectedKeys,
  onLayerChange,
  canManageLayers = true,
  moduleId,
  dock = 'float',
  onDockChange,
  dockSize = 0.5,
  onDockSizeChange,
}: Props) {
  const docked = dock !== 'float';
  /* ── Data (unchanged behaviour) ─────────────────────────────────────── */
  const [records, setRecords] = useState<AttributeFeature[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  /**
   * Bumped after a field is added to an ArcGIS-backed layer — the one case
   * neither `layer.localData` (ArcGIS layers have none) nor any other prop
   * changes on its own, since the service is the source of truth and
   * `addFieldToLayer` has nothing local to hand back. A local layer needs no
   * such nudge: `onLayerChange` gives it a new `localData`, which is already
   * in both effects' own dependency lists below.
   */
  const [schemaRefreshToken, setSchemaRefreshToken] = useState(0);

  /* ── Inline attribute editing ──────────────────────────────────────────
   * What the service actually permits — read once per layer, the same way
   * the Edit tool panel reads it, so the pencil never offers a save the
   * server (or a read-only WMS/MapServer layer) will refuse. */
  const [editInfo, setEditInfo] = useState<ServiceEditInfo | null>(null);
  useEffect(() => {
    let live = true;
    setEditInfo(null);
    readEditInfo(layer, arcgisUrl).then((info) => {
      if (live) setEditInfo(info);
    });
    return () => {
      live = false;
    };
    // `layer.localData` (not the whole `layer`) so adding a field — which
    // writes it onto every feature — is picked up, without re-fetching an
    // ArcGIS service's metadata for an unrelated change like opacity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer.id, layer.arcgisLayerId, layer.localData, arcgisUrl, schemaRefreshToken]);

  /* A layer this window can actually write to — mirrors EditPanel's own
   *  `canEditLayer`/`canEditAttributes` exactly, not just the first half of
   *  it: the shared map is guarded by `canManageLayers`, and a layer someone
   *  shared with THIS user stays reachable in the viewer too, but EITHER
   *  way now also needs the signed-in user's own role to actually hold
   *  `data:edit`/`data:edit-attributes` — this table's pencil, "Add field"
   *  and Field Calculator used to skip that second half entirely, so anyone
   *  a layer was merely shared with with could edit (and, once it existed,
   *  run Field Calculator on) it regardless of their role's own grants. */
  const { can } = usePermissions();
  const canEditAttributesPermission = can('data:edit') || can('data:edit-attributes');
  const canEditLayer = canManageLayers || Boolean(layer.sharedLayerId);
  const canEditThisLayer =
    Boolean(onLayerChange) && canEditLayer && canEditAttributesPermission && Boolean(editInfo?.editable);

  const [editingRecord, setEditingRecord] = useState<AttributeFeature | null>(null);
  const [editDraft, setEditDraft] = useState<Record<string, unknown>>({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const openEditRecord = (record: AttributeFeature) => {
    setEditingRecord(record);
    setEditDraft({ ...record.properties });
    setEditError(null);
  };

  const saveEditRecord = async () => {
    if (!editingRecord || !editInfo) return;
    setEditSaving(true);
    setEditError(null);

    /* The table's own row id is `feature.id` when the source set one (an
       ArcGIS OID, or a file format like OSM export that stamps its own id)
       and only falls back to array position otherwise — see the load effect
       above. For a LOCAL layer that is NOT necessarily this feature's real
       position in `layer.localData.features`, especially with a filter
       active (`filteredLocalFeatures` returns a subset). `featureKey` is
       the one identity both sides already agree on — the same key the
       selection-sync logic above trusts — so it re-finds the true index
       rather than assuming the row id is one. */
    let recordId = editingRecord.id;
    if (layer.localData && editInfo.objectIdField === LOCAL_ID_FIELD) {
      const trueIndex = (layer.localData.features ?? []).findIndex(
        (feature) => featureKey(feature, layer.id) === editingRecord.key,
      );
      if (trueIndex === -1) {
        setEditError('That feature is no longer in this layer.');
        setEditSaving(false);
        return;
      }
      recordId = String(trueIndex);
    }

    const result = await saveFeatureAttributesUpdate(
      layer,
      editInfo,
      recordId,
      editInfo.fields,
      editDraft,
      moduleId,
      arcgisUrl,
    );
    setEditSaving(false);
    if (!result.success) {
      setEditError(result.message);
      return;
    }
    if (result.updatedLayer) onLayerChange?.(layer.id, result.updatedLayer);
    // Reflected immediately rather than waiting for the load effect to
    // re-run (it will too, once `layer.localData` changes upstream) — a
    // save should look instant, not pending on a round trip back down.
    setRecords((current) =>
      current.map((row) =>
        row.id === editingRecord.id
          ? { ...row, ...editDraft, properties: { ...row.properties, ...editDraft } }
          : row,
      ),
    );
    setEditingRecord(null);
  };

  /* ── Add a field ────────────────────────────────────────────────────── */
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldAlias, setNewFieldAlias] = useState('');
  const [newFieldType, setNewFieldType] = useState<NewFieldDef['type']>('string');
  const [addFieldSaving, setAddFieldSaving] = useState(false);
  const [addFieldError, setAddFieldError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  /* ── Column header: sort + right-click menu + Field Calculator ───────── */
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  /** The column a right-click opened a menu for, and where — `point` drives
   *  LayerOptionsMenu's own click-coordinate positioning (see its own doc
   *  on why a context menu has no anchor ELEMENT to measure from). */
  const [columnMenu, setColumnMenu] = useState<{ column: string; point: { x: number; y: number } } | null>(null);
  const [calculatorField, setCalculatorField] = useState<string | undefined>(undefined);
  const [calculatorOpen, setCalculatorOpen] = useState(false);

  const submitAddField = async () => {
    const name = newFieldName.trim();
    if (!name) {
      setAddFieldError('Field name is required.');
      return;
    }
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) {
      setAddFieldError('Use letters, numbers and underscores only, starting with a letter.');
      return;
    }
    setAddFieldSaving(true);
    setAddFieldError(null);
    const result = await addFieldToLayer(
      layer,
      { name, alias: newFieldAlias.trim() || name, type: newFieldType },
      arcgisUrl,
    );
    setAddFieldSaving(false);
    if (!result.success) {
      setAddFieldError(result.message);
      return;
    }
    if (result.updatedLayer) {
      onLayerChange?.(layer.id, result.updatedLayer);
    } else {
      // ArcGIS branch: nothing local changed, so nothing else here would
      // notice the service now has one more field. Nudge both effects that
      // read `schemaRefreshToken` to re-request.
      setSchemaRefreshToken((token) => token + 1);
    }
    setAddFieldOpen(false);
    setNewFieldName('');
    setNewFieldAlias('');
    setNewFieldType('string');
  };

  useEffect(() => {
    let isCurrent = true;
    setStatus('loading');
    setRecords([]);
    setSelectedIds(new Set());
    onSelectFeature?.(null);

    const loadAttributes = async () => {
      try {
        // A file-backed layer already holds its features; there is no service
        // to query, so read them directly instead of issuing a doomed request.
        if (layer.localData) {
          /* Filtered, exactly as the two service branches below ask their
             services to filter. Without this the table answered a different
             question from the map: 125 features drawn, all 400 listed. */
          const features = filteredLocalFeatures(layer);
          setRecords(
            features.map((feature, index) => ({
              ...(feature.properties ?? {}),
              id: String(feature.id ?? index),
              key: featureKey(feature, layer.id),
              properties: (feature.properties ?? {}) as Record<string, unknown>,
              geometry: feature.geometry ?? undefined,
            })),
          );
          setStatus('ready');
          return;
        }

        // ArcGIS layers have no WFS endpoint; their attributes come from `query`.
        const isArcgisLayer = layer.arcgisLayerId !== undefined;
        let requestUrl = '';

        if (isArcgisLayer) {
          // The layer's OWN service, when it was added from a different one
          // than the module's primary `arcgisUrl` — see `arcgisServiceUrlFor`.
          const effectiveUrl = arcgisServiceUrlFor(layer, { arcgisUrl });
          const baseUrl = getArcgisBaseUrl(effectiveUrl);
          if (!baseUrl) throw new Error('Missing ArcGIS service URL');
          const params = new URLSearchParams({
            // The layer's filter, not a blanket 1=1 — the table has to answer
            // the same question the map is showing.
            where: layer.filter?.trim() || '1=1',
            outFields: '*',
            outSR: '4326',
            f: 'geojson',
            resultRecordCount: '2000',
          });
          requestUrl = withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/query?${params.toString()}`, effectiveUrl);
        } else {
          requestUrl = getWfsUrl(geoServerUrl, layer.name, layer.filter);
        }

        const response = await fetch(requestUrl);
        if (!response.ok) throw new Error('Unable to load attributes');
        const result = (await response.json()) as {
          features?: {
            id?: string | number;
            properties?: Record<string, unknown>;
            geometry?: GeoJSON.Geometry;
          }[];
        };
        if (!isCurrent) return;
        setRecords(
          result.features?.map((feature, index) => ({
            ...feature.properties,
            id: String(feature.id ?? index),
            key: featureKey(feature, layer.id),
            properties: feature.properties || {},
            geometry: feature.geometry,
          })) || [],
        );
        setStatus('ready');
      } catch {
        if (isCurrent) setStatus('error');
      }
    };

    void loadAttributes();
    return () => {
      isCurrent = false;
    };
    // Re-runs when the window is pointed at a different layer, which is how
    // "open the table for another layer" swaps content in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `layer.filter` is in the list on purpose: applying or clearing a filter
    // has to re-request, or the window keeps showing the previous answer.
    // `layer.filterClauses` joins the list for the same reason `layer.filter`
    // is on it: a file-backed layer's filter lives there, and applying or
    // clearing it has to rebuild the rows. `schemaRefreshToken` re-requests
    // after a field is added to an ArcGIS-backed layer — see its own doc
    // comment above.
  }, [geoServerUrl, arcgisUrl, layer.id, layer.name, layer.arcgisLayerId, layer.localData,
      layer.filter, layer.filterClauses, schemaRefreshToken]);

  const columns = useMemo(
    () => Array.from(new Set(records.flatMap((record) => Object.keys(record.properties)))),
    [records],
  );
  const selectedRecords = records.filter((record) => selectedIds.has(record.id));

  /**
   * Ticks the rows a tool asked for.
   *
   * Held in a ref and applied when the ROWS arrive, which is the whole point.
   * This window issues its own request, so on the render where it mounts it
   * has no records to match against — and the report effect below used to fire
   * first with an empty set, telling the app nothing was selected and wiping
   * the very keys it had just been handed. The pending value survives that.
   */
  const pendingSelectionRef = useRef<string[] | null>(null);
  const appliedSelectionRef = useRef<string | null>(null);
  /**
   * Set while the table is MIRRORING a selection it was handed.
   *
   * Without it the mirror echoes: applying the keys changes `selectedIds`,
   * which fires the report below, which publishes whatever rows this window
   * happens to hold — and this window loads a capped page. A selection of 500
   * features would come back as the 200 the table had read, silently shrinking
   * it everywhere else. A mirror must not answer back.
   */
  const echoGuardRef = useRef(false);

  useEffect(() => {
    const signature = (preselectedKeys ?? []).join('|');
    if (signature === appliedSelectionRef.current) return;

    // Rows not in yet — remember the request and apply it when they arrive.
    if (records.length === 0) {
      pendingSelectionRef.current = preselectedKeys ?? [];
      return;
    }
    if (!preselectedKeys || preselectedKeys.length === 0) return;

    const wanted = new Set(preselectedKeys);
    const matchedIds = new Set(
      records.filter((record) => record.key && wanted.has(record.key)).map((r) => r.id),
    );

    /*
     * `preselectedKeys` is fed by the SAME `onSelectionChange` this table
     * calls — ticking a row reports it up, which comes right back down as
     * `preselectedKeys`, landing here. Previously this ALWAYS re-applied
     * (via `echoGuardRef`, a single mutable flag assuming the resulting
     * re-render settles before the next click), which — on every ordinary
     * manual tick — redundantly rebuilt `selectedIds` from a NEW Set
     * instance holding the exact same ids AND force-switched the view to
     * "Selected" the user never asked for, with a real race if a second
     * click landed before that round trip's echo-guard had been consumed:
     * the guard, still true, would swallow the SECOND click's own report
     * too, leaving the map's highlight one tick behind what the table
     * showed. Comparing by VALUE first (not reference) means an echo of
     * the table's own current selection is a genuine no-op — nothing to
     * apply, nothing to guard — and only a selection that actually differs
     * (Select by location, a query result, or opening the table fresh)
     * takes the apply-and-switch-to-Selected path below.
     */
    const unchanged =
      matchedIds.size === selectedIds.size && [...matchedIds].every((id) => selectedIds.has(id));
    appliedSelectionRef.current = signature;
    pendingSelectionRef.current = null;
    if (unchanged) return;

    echoGuardRef.current = true;
    setSelectedIds(matchedIds);
    /* Showing only the matches is why someone opened the table after a query
       or a selection box — the answer, not the haystack it came from. */
    setView('selected');
    /* Depends on `preselectedKeys` as well as `records`: drawing a second
       selection box changes the keys while the rows stay put, and the previous
       version only re-ran on rows — so the table ignored every box after the
       first. */
  }, [records, preselectedKeys, selectedIds]);

  /* Keeps the map's highlight in step with the table. Reported from an effect
     rather than from each toggle handler so every path that changes the
     selection — a row, the header tick, Select by attribute — is covered by
     one line instead of four.

     Silent until the rows are in: an empty report during loading is not "the
     user deselected everything", it is "this window has not read anything
     yet", and the two must not look the same to the rest of the app. */
  useEffect(() => {
    if (records.length === 0) return;
    if (pendingSelectionRef.current) return;
    if (echoGuardRef.current) {
      echoGuardRef.current = false;
      return;
    }
    onSelectionChange?.(selectedRecords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, records]);

  /* ── Free-text search ───────────────────────────────────────────────
     Filters the loaded rows across EVERY attribute at once. This is the
     question people actually arrive with — "where does Webster appear?" —
     and the clause builder below cannot express it, because a clause has to
     name a field first.

     A view filter, not a selection: rows that scroll out of sight stay
     selected, so searching cannot silently drop what someone already picked.
     Space-separated terms are ANDed, and a term may match a DIFFERENT column
     from its neighbour — "webster building" finds the row whose street is
     Webster and whose type is Building. */
  const [search, setSearch] = useState('');
  /**
   * Which half of the table is on show.
   *
   * A selection scattered through two thousand rows is a selection nobody can
   * read. This is the same pair every desktop GIS puts under its table, and it
   * is what makes "the query picked these" inspectable rather than merely true.
   */
  const [view, setView] = useState<'all' | 'selected'>('all');

  const searchTerms = useMemo(
    () => search.toLowerCase().split(/\s+/).filter(Boolean),
    [search],
  );

  /** Each row flattened to one lowercase string, rebuilt only when rows change
   *  — not per keystroke, which on a 2,000-row table is the difference between
   *  instant and janky. */
  const haystacks = useMemo(
    () =>
      records.map((record) =>
        Object.values(record.properties)
          .map((value) =>
            value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''),
          )
          // A separator no value can contain, so a term cannot match across
          // the seam between two columns.
          .join('')
          .toLowerCase(),
      ),
    [records],
  );

  const visibleRecords = useMemo(() => {
    // The two filters compose: searching inside a selection narrows the
    // selection, it does not escape back to every row.
    const scoped =
      view === 'selected' ? records.filter((record) => selectedIds.has(record.id)) : records;
    const searched =
      searchTerms.length === 0
        ? scoped
        : (() => {
            const index = new Map(records.map((record, position) => [record.id, position]));
            return scoped.filter((record) =>
              searchTerms.every((term) => (haystacks[index.get(record.id) ?? 0] ?? '').includes(term)),
            );
          })();

    if (!sort) return searched;
    const { field, direction } = sort;
    // Blank/missing always sorts last, in EITHER direction — "Z to A" on a
    // column with gaps should surface the real values first, not open on a
    // page of blanks, the same convention a spreadsheet's own sort uses.
    const sorted = [...searched].sort((a, b) => {
      const left = a.properties[field];
      const right = b.properties[field];
      const leftBlank = left === null || left === undefined || left === '';
      const rightBlank = right === null || right === undefined || right === '';
      if (leftBlank && rightBlank) return 0;
      if (leftBlank) return 1;
      if (rightBlank) return -1;

      const bothNumeric = typeof left === 'number' && typeof right === 'number';
      const comparison = bothNumeric
        ? (left as number) - (right as number)
        : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
      return direction === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [records, haystacks, searchTerms, view, selectedIds, sort]);

  /** Whether one rendered cell is why its row survived the search. */
  const cellMatches = (text: string) => {
    if (searchTerms.length === 0) return false;
    const lower = text.toLowerCase();
    return searchTerms.some((term) => lower.includes(term));
  };

  /* ── Select by attribute ────────────────────────────────────────────
     Evaluated against the records this window has LOADED, not against the
     whole layer — the table itself is capped, and a selection that silently
     covered rows the user cannot see would be worse than one that says so.
     The expression is structured (see attributeQuery.ts) so the same builder
     can be pushed to the service once server-side selection exists. */
  const [queryOpen, setQueryOpen] = useState(false);
  const [clauses, setClauses] = useState<AttributeClause[]>([]);

  const newClause = useCallback(
    (): AttributeClause => ({
      id: `clause_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      field: '',
      operator: 'equals',
      value: '',
      conjunction: 'and',
    }),
    [],
  );

  const openQuery = () => {
    setClauses((current) => (current.length === 0 ? [newClause()] : current));
    setQueryOpen(true);
  };

  const updateClause = (id: string, changes: Partial<AttributeClause>) =>
    setClauses((current) =>
      current.map((clause) => (clause.id === id ? { ...clause, ...changes } : clause)),
    );

  const matchingIds = useMemo(() => {
    if (clauses.filter(clauseIsComplete).length === 0) return [];
    return records
      .filter((record) => matchesClauses(record.properties, clauses))
      .map((record) => record.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, clauses]);

  /** New / add / remove, the three selection verbs every desktop GIS has. */
  const applySelection = (mode: 'new' | 'add' | 'remove') => {
    setSelectedIds((current) => {
      if (mode === 'new') return new Set(matchingIds);
      const next = new Set(current);
      for (const id of matchingIds) {
        if (mode === 'add') next.add(id);
        else next.delete(id);
      }
      return next;
    });
    onSelectFeature?.(null);
  };

  const selectRecord = (record: AttributeFeature) => {
    const next = new Set(selectedIds);
    if (next.has(record.id)) {
      next.delete(record.id);
      onSelectFeature?.(null);
    } else {
      next.add(record.id);
      onSelectFeature?.(record);
    }
    setSelectedIds(next);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    onSelectFeature?.(null);
    // Otherwise the table sits on an empty "Selected" view with no way back
    // that reads as obvious.
    setView('all');

    /* Forget what a tool asked for, or the pending value would re-tick these
       rows the moment the records list changed and Clear would look ignored.
       Clearing here clears the map highlight too — the table and the map show
       one selection, not two that drift apart. */
    pendingSelectionRef.current = null;
    appliedSelectionRef.current = null;
    onSelectionChange?.([]);
  };

  const exportCsv = () => {
    // Selection first, then whatever the search has left on screen. Exporting
    // all 2,000 loaded rows while the table shows 12 would not match what the
    // person is looking at.
    const rows = selectedRecords.length > 0 ? selectedRecords : visibleRecords;
    const cell = (value: unknown) =>
      `"${String(typeof value === 'object' ? JSON.stringify(value) : (value ?? '')).replace(/"/g, '""')}"`;
    const csv = [
      columns.map(cell).join(','),
      ...rows.map((record) => columns.map((column) => cell(record.properties[column])).join(',')),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${layer.name || 'layer'}-attributes.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /* ── Window geometry ────────────────────────────────────────────────── */
  const windowRef = useRef<HTMLDivElement | null>(null);
  /** The map container; the window is positioned and clamped inside it. */
  const [bounds, setBounds] = useState({ width: 0, height: 0 });
  const [geometry, setGeometry] = useState<Geometry | null>(null);
  const [minimised, setMinimised] = useState(false);
  const [maximised, setMaximised] = useState(false);
  /** Geometry to restore when un-maximising. */
  const restoreRef = useRef<Geometry | null>(null);

  // Measure the map container (the offsetParent) so the window can be clamped
  // to it rather than to the viewport.
  useEffect(() => {
    const parent = windowRef.current?.parentElement;
    if (!parent) return undefined;

    const measure = () => {
      const rect = parent.getBoundingClientRect();
      setBounds({ width: rect.width, height: rect.height });
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(parent);
    return () => observer.disconnect();
  }, []);

  // First open: bottom-centre, clear of the top-left/top-right map controls.
  useEffect(() => {
    if (geometry || bounds.width === 0) return;
    const width = Math.min(Math.max(MIN_WIDTH, bounds.width * 0.62), bounds.width - 32);
    const height = Math.min(Math.max(MIN_HEIGHT, bounds.height * 0.42), bounds.height - 32);
    setGeometry({
      x: Math.max(16, (bounds.width - width) / 2),
      y: Math.max(16, bounds.height - height - 24),
      width,
      height,
    });
  }, [bounds, geometry]);

  const clamp = useCallback(
    (next: Geometry): Geometry => {
      const width = Math.max(MIN_WIDTH, Math.min(next.width, bounds.width || next.width));
      const height = Math.max(MIN_HEIGHT, Math.min(next.height, bounds.height || next.height));
      return {
        width,
        height,
        // Always leave a grabbable strip on screen so the window cannot be
        // dragged somewhere it can never be recovered from.
        x: Math.max(KEEP_VISIBLE - width, Math.min(next.x, (bounds.width || width) - KEEP_VISIBLE)),
        y: Math.max(0, Math.min(next.y, (bounds.height || height) - 40)),
      };
    },
    [bounds],
  );

  /** Pointer-driven move/resize. Pointer events cover mouse, touch, and pen. */
  const dragRef = useRef<{
    mode: 'move' | 'resize-se' | 'resize-e' | 'resize-s';
    startX: number;
    startY: number;
    start: Geometry;
  } | null>(null);

  const beginInteraction = (
    event: React.PointerEvent,
    mode: 'move' | 'resize-se' | 'resize-e' | 'resize-s',
  ) => {
    if (!geometry || maximised || docked) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      mode,
      startX: event.clientX,
      startY: event.clientY,
      start: geometry,
    };
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  };

  useEffect(() => {
    const handleMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;

      setGeometry(
        clamp(
          drag.mode === 'move'
            ? { ...drag.start, x: drag.start.x + dx, y: drag.start.y + dy }
            : {
                ...drag.start,
                width: drag.mode === 'resize-s' ? drag.start.width : drag.start.width + dx,
                height: drag.mode === 'resize-e' ? drag.start.height : drag.start.height + dy,
              },
        ),
      );
    };
    const stop = () => {
      dragRef.current = null;
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, [clamp]);

  /** Dragging the divider on a docked panel's map-facing edge — a single
   *  axis, reported as a fraction of the FULL container (`windowRef`'s own
   *  parent, the same one `bounds` already measures), not pixels: the
   *  panel's actual width/height is always a percentage (see the style
   *  below), so the value that means anything to resize BY has to be one
   *  too, or it would drift the moment the window itself was resized. */
  const dockDragRef = useRef<{ startX: number; startY: number } | null>(null);

  const beginDockResize = (event: React.PointerEvent) => {
    if (!onDockSizeChange) return;
    event.preventDefault();
    event.stopPropagation();
    dockDragRef.current = { startX: event.clientX, startY: event.clientY };
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  };

  useEffect(() => {
    const handleMove = (event: PointerEvent) => {
      if (!dockDragRef.current || !onDockSizeChange) return;
      const parent = windowRef.current?.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      // The panel occupies the space from the container's far edge back to
      // the divider — so its fraction is the distance from the POINTER to
      // that far edge, not from the container's near edge.
      const fraction =
        dock === 'right'
          ? (rect.right - event.clientX) / rect.width
          : (rect.bottom - event.clientY) / rect.height;
      onDockSizeChange(Math.min(MAX_DOCK_FRACTION, Math.max(MIN_DOCK_FRACTION, fraction)));
    };
    const stop = () => {
      dockDragRef.current = null;
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, [dock, onDockSizeChange]);

  const toggleMaximise = () => {
    if (!geometry) return;
    if (maximised) {
      setGeometry(restoreRef.current ?? geometry);
      setMaximised(false);
      return;
    }
    restoreRef.current = geometry;
    setGeometry({ x: 8, y: 8, width: bounds.width - 16, height: bounds.height - 16 });
    setMaximised(true);
    setMinimised(false);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!geometry && !docked) {
    // Waiting on the first container measurement; nothing to place yet.
    return <div ref={windowRef} className="hidden" />;
  }

  const title = getLayerDisplayName(layer);

  return (
    <>
    <div
      ref={windowRef}
      role="dialog"
      aria-label={`Attribute table for ${title}`}
      className={`gl-panel gl-surface--strong absolute flex flex-col overflow-hidden ${
        docked ? '' : 'gl-surface--over-map'
      }`}
      style={{
        ...(docked
          ? // Fills exactly the fraction of the FULL container the map's own
            // box just made room for (see the map-sizing `<div>` in
            // App.tsx) — not a size relative to this element's own parent,
            // which is still the full, never-resized outer container.
            dock === 'right'
            ? { top: 0, right: 0, bottom: 0, width: `${dockSize * 100}%` }
            : { left: 0, right: 0, bottom: 0, height: `${dockSize * 100}%` }
          : {
              left: geometry!.x,
              top: geometry!.y,
              width: geometry!.width,
              height: minimised ? 44 : geometry!.height,
            }),
        zIndex: 'var(--z-floating-window)' as unknown as number,
        // A docked pane sits flush against the map, not floating over it — no
        // rounded corner on the map-facing edge would look odd, but a plain
        // square panel reads as "part of the layout" the way ArcGIS Pro's own
        // docked panes do, so the radius is dropped entirely while docked
        // rather than picking which corners to keep for which side.
        borderRadius: docked ? 0 : 'var(--glass-radius-lg)',
        background: 'var(--glass-bg-strong)',
        backdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
        WebkitBackdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
        border: '1px solid var(--glass-border)',
        boxShadow: docked ? 'none' : 'var(--glass-shadow-lg)',
        // Only the window captures pointer input; the rest of the map stays live.
        pointerEvents: 'auto',
        transition: dragRef.current ? 'none' : 'height 160ms ease',
      }}
      // Stop clicks reaching the map underneath (feature identify etc.).
      onPointerDown={(event) => event.stopPropagation()}
    >
      {/* ── Title bar: the only drag handle (floating mode only — a docked
          pane is sized by its parent, nothing here to drag) ────────────── */}
      <div
        onPointerDown={(event) => {
          if (docked) return;
          // Buttons inside the bar must not start a drag.
          if ((event.target as HTMLElement).closest('button')) return;
          beginInteraction(event, 'move');
        }}
        onDoubleClick={docked ? undefined : toggleMaximise}
        className="gl-header flex flex-shrink-0 items-center gap-2 px-3 py-2"
        style={{ cursor: docked || maximised ? 'default' : 'move', touchAction: 'none' }}
      >
        <Table2 className="h-4 w-4 flex-shrink-0 text-blue-600" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold leading-tight text-slate-800" title={title}>
            {title}
          </p>
          <p className="truncate text-[10px] leading-tight text-slate-400" title={layer.filter}>
            {layer.filter?.trim() ? 'Filtered' : 'Attribute table'}
            {status === 'ready' &&
              (searchTerms.length > 0
                ? ` · ${visibleRecords.length} of ${records.length} shown`
                : ` · ${records.length} record${records.length === 1 ? '' : 's'}`)}
            {selectedRecords.length > 0 && ` · ${selectedRecords.length} selected`}
          </p>
        </div>

        <button
          type="button"
          onClick={() => (queryOpen ? setQueryOpen(false) : openQuery())}
          aria-expanded={queryOpen}
          title="Select rows by an attribute expression"
          className={`gl-btn px-2 py-1 text-[11px] ${
            queryOpen ? 'gl-btn--primary' : 'gl-btn--ghost'
          }`}
        >
          <ListFilter className="h-3.5 w-3.5" />
          Select by attribute
        </button>
        <button
          type="button"
          onClick={() => onZoomToSelection?.(selectedRecords)}
          disabled={selectedRecords.length === 0}
          className="gl-btn gl-btn--ghost px-2 py-1 text-[11px]"
        >
          Zoom to selected
        </button>
        <button
          type="button"
          onClick={clearSelection}
          disabled={selectedRecords.length === 0}
          className="gl-btn gl-btn--ghost px-2 py-1 text-[11px]"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={exportCsv}
          title="Download CSV"
          aria-label="Download CSV"
          className="gl-btn gl-btn--ghost px-1.5 py-1"
        >
          <Download className="h-3.5 w-3.5" />
        </button>

        {canEditThisLayer && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              title="More"
              aria-label="More options"
              aria-expanded={menuOpen}
              className="gl-btn gl-btn--ghost px-1.5 py-1"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
            {menuOpen && (
              <>
                {/* A full-screen click-catcher, same trick the layer options
                    menu elsewhere in the app uses to close on an outside click. */}
                <div className="fixed inset-0 z-[3000]" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-full z-[3001] mt-1 w-44 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setAddFieldError(null);
                      setAddFieldOpen(true);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] text-slate-700 hover:bg-slate-50"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add field{'…'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setCalculatorField(undefined);
                      setCalculatorOpen(true);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] text-slate-700 hover:bg-slate-50"
                  >
                    <Calculator className="h-3.5 w-3.5" />
                    Field Calculator{'…'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <span className="mx-0.5 h-4 w-px flex-shrink-0 bg-slate-200" />

        {/* Dock toggles — ArcGIS Pro-style: pick a side to snap the table into
            a genuine, fixed half of the map area (the parent, App.tsx, is what
            actually resizes the map container; this only reports the choice).
            Clicking the already-active one undocks back to a floating window. */}
        {onDockChange && (
          <>
            <button
              type="button"
              onClick={() => {
                onDockChange(dock === 'right' ? 'float' : 'right');
                setMinimised(false);
              }}
              title={dock === 'right' ? 'Undock' : 'Dock to the right'}
              aria-pressed={dock === 'right'}
              aria-label={dock === 'right' ? 'Undock attribute table' : 'Dock attribute table to the right'}
              className={`gl-btn px-1.5 py-1 ${dock === 'right' ? 'gl-btn--primary' : 'gl-btn--ghost'}`}
            >
              <PanelRight className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => {
                onDockChange(dock === 'bottom' ? 'float' : 'bottom');
                setMinimised(false);
              }}
              title={dock === 'bottom' ? 'Undock' : 'Dock to the bottom'}
              aria-pressed={dock === 'bottom'}
              aria-label={dock === 'bottom' ? 'Undock attribute table' : 'Dock attribute table to the bottom'}
              className={`gl-btn px-1.5 py-1 ${dock === 'bottom' ? 'gl-btn--primary' : 'gl-btn--ghost'}`}
            >
              <PanelBottom className="h-3.5 w-3.5" />
            </button>
          </>
        )}

        {/* Minimise/maximise apply only to a floating window — a docked pane
            is already exactly the size its parent gives it. */}
        {!docked && (
          <>
            <button
              type="button"
              onClick={() => setMinimised((value) => !value)}
              title={minimised ? 'Restore' : 'Minimise'}
              aria-label={minimised ? 'Restore attribute table' : 'Minimise attribute table'}
              className="gl-btn gl-btn--ghost px-1.5 py-1"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={toggleMaximise}
              title={maximised ? 'Restore' : 'Maximise'}
              aria-label={maximised ? 'Restore attribute table' : 'Maximise attribute table'}
              className="gl-btn gl-btn--ghost px-1.5 py-1"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onClose}
          title="Close"
          aria-label="Close attribute table"
          className="gl-btn gl-btn--ghost px-1.5 py-1"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* ── Search ────────────────────────────────────────────────────
          Always visible rather than behind a toggle: on a table this is the
          first thing most people reach for, and a hidden search is a search
          nobody finds. */}
      {!minimised && (
        <div className="flex flex-shrink-0 items-center gap-2 border-b border-slate-200 px-3 py-1.5">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setSearch('');
              }}
              placeholder="Search all attributes…"
              aria-label="Search all attributes"
              className="gl-input w-full py-1 pl-7 pr-7 text-[12px]"
            />
            {search !== '' && (
              <button
                type="button"
                onClick={() => setSearch('')}
                title="Clear search"
                aria-label="Clear search"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          {status === 'ready' && searchTerms.length > 0 && (
            <span className="flex-shrink-0 text-[11px] tabular-nums text-slate-500">
              {visibleRecords.length} of {records.length}
            </span>
          )}
        </div>
      )}

      {/* ── Content ───────────────────────────────────────────────────── */}
      {!minimised && queryOpen && (
        <div className="flex-shrink-0 border-b border-slate-200 bg-slate-50 px-3 py-2.5">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Select by attribute
            </p>
            <button
              type="button"
              onClick={() => setQueryOpen(false)}
              aria-label="Close select by attribute"
              className="rounded p-0.5 text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="space-y-1.5">
            {clauses.map((clause, index) => {
              const values = distinctValues(records, clause.field);
              const listId = `values-${clause.id}`;
              return (
                <div key={clause.id} className="flex flex-wrap items-center gap-1.5">
                  {index === 0 ? (
                    <span className="w-14 flex-shrink-0 text-[11px] font-medium text-slate-500">
                      Where
                    </span>
                  ) : (
                    <select
                      value={clause.conjunction}
                      onChange={(event) =>
                        updateClause(clause.id, {
                          conjunction: event.target.value as 'and' | 'or',
                        })
                      }
                      aria-label="Join with the clause above"
                      className="w-14 flex-shrink-0 rounded border border-slate-300 bg-white px-1 py-1 text-[11px] text-slate-700 outline-none focus:border-blue-400"
                    >
                      <option value="and">And</option>
                      <option value="or">Or</option>
                    </select>
                  )}

                  <select
                    value={clause.field}
                    onChange={(event) => updateClause(clause.id, { field: event.target.value })}
                    aria-label="Field"
                    className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-1.5 py-1 text-[11px] text-slate-700 outline-none focus:border-blue-400"
                  >
                    <option value="">Field…</option>
                    {columns.map((column) => (
                      <option key={column} value={column}>
                        {column}
                      </option>
                    ))}
                  </select>

                  <select
                    value={clause.operator}
                    onChange={(event) =>
                      updateClause(clause.id, {
                        operator: event.target.value as AttributeOperator,
                      })
                    }
                    aria-label="Operator"
                    className="rounded border border-slate-300 bg-white px-1.5 py-1 text-[11px] text-slate-700 outline-none focus:border-blue-400"
                  >
                    {ATTRIBUTE_OPERATORS.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.label}
                      </option>
                    ))}
                  </select>

                  {operatorNeedsValue(clause.operator) && (
                    <>
                      {/* A datalist, not a dropdown: the loaded values are a
                          shortcut, and typing a value the table has not loaded
                          must stay possible. */}
                      <input
                        value={clause.value}
                        onChange={(event) => updateClause(clause.id, { value: event.target.value })}
                        list={values.length > 0 ? listId : undefined}
                        placeholder="Value"
                        aria-label="Value"
                        className="w-28 rounded border border-slate-300 bg-white px-1.5 py-1 text-[11px] text-slate-700 outline-none focus:border-blue-400"
                      />
                      {values.length > 0 && (
                        <datalist id={listId}>
                          {values.map((value) => (
                            <option key={value} value={value} />
                          ))}
                        </datalist>
                      )}
                    </>
                  )}

                  <button
                    type="button"
                    onClick={() =>
                      setClauses((current) =>
                        current.length === 1
                          ? [newClause()]
                          : current.filter((entry) => entry.id !== clause.id),
                      )
                    }
                    aria-label="Remove clause"
                    className="rounded p-0.5 text-slate-400 transition hover:bg-slate-200 hover:text-red-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setClauses((current) => [...current, newClause()])}
              className="gl-btn gl-btn--ghost px-2 py-1 text-[11px]"
            >
              <Plus className="h-3 w-3" />
              Add clause
            </button>

            <span className="mx-0.5 h-4 w-px bg-slate-300" />

            <button
              type="button"
              onClick={() => applySelection('new')}
              disabled={matchingIds.length === 0}
              className="gl-btn gl-btn--primary px-2.5 py-1 text-[11px]"
            >
              Select {matchingIds.length > 0 ? matchingIds.length : ''}
            </button>
            <button
              type="button"
              onClick={() => applySelection('add')}
              disabled={matchingIds.length === 0}
              className="gl-btn gl-btn--ghost px-2 py-1 text-[11px]"
            >
              Add to selection
            </button>
            <button
              type="button"
              onClick={() => applySelection('remove')}
              disabled={matchingIds.length === 0 || selectedRecords.length === 0}
              className="gl-btn gl-btn--ghost px-2 py-1 text-[11px]"
            >
              Remove from selection
            </button>
          </div>

          <p className="mt-1.5 text-[10px] leading-snug text-slate-400">
            {clauses.filter(clauseIsComplete).length === 0 ? (
              'Build an expression to find rows — for example, diameter is at least 63.'
            ) : (
              <>
                <span className="font-medium text-slate-500">{describeClauses(clauses)}</span>
                {' — '}
                {matchingIds.length} of {records.length} loaded row
                {records.length === 1 ? '' : 's'} match. Clauses are read left to right.
              </>
            )}
          </p>
        </div>
      )}

      {!minimised && (
        <div
          className="min-h-0 flex-1 overflow-auto"
          // Keeps wheel/touch scrolling inside the table instead of chaining
          // out to the page once the ends are reached.
          style={{ overscrollBehavior: 'contain' }}
        >
          {status === 'loading' && (
            <p className="p-6 text-sm text-slate-500">Loading attributes…</p>
          )}
          {status === 'error' && (
            <p className="p-6 text-sm text-red-600">
              Attributes could not be loaded for this layer.
            </p>
          )}
          {status === 'ready' && columns.length === 0 && (
            <p className="p-6 text-sm text-slate-500">No attributes found.</p>
          )}
          {status === 'ready' && columns.length > 0 && visibleRecords.length === 0 && (
            <div className="p-6">
              <p className="text-sm text-slate-500">
                {searchTerms.length === 0
                  ? (preselectedKeys?.length ?? 0) > 0
                    ? `${preselectedKeys!.length} features are selected, but none of them are among the ${records.length.toLocaleString()} rows this window loaded.`
                    : 'Nothing is selected yet. Tick a row, or run a query from the Query tool.'
                  : `Nothing in the ${
                      view === 'selected' ? 'selected rows' : `${records.length.toLocaleString()} loaded rows`
                    } matches “${search.trim()}”.`}
              </p>
              {searchTerms.length > 0 && (
                <>
                  <p className="mt-1 text-xs text-slate-400">
                    Every term has to appear somewhere in the row. Drop a word to widen the search.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="gl-btn gl-btn--secondary mt-3 px-2.5 py-1 text-[11px]"
                  >
                    Clear search
                  </button>
                </>
              )}
            </div>
          )}
          {status === 'ready' && columns.length > 0 && visibleRecords.length > 0 && (
            <table className="min-w-full text-left text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50/95 text-xs uppercase tracking-wide text-slate-500 backdrop-blur">
                <tr>
                  <th className="w-10 border-b border-slate-200 px-3 py-2.5 font-semibold">
                    {/* Acts on the rows currently SHOWN, so ticking it while
                        a search is on selects the matches and nothing else. */}
                    <input
                      type="checkbox"
                      checked={
                        visibleRecords.length > 0 &&
                        visibleRecords.every((record) => selectedIds.has(record.id))
                      }
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedIds(new Set(visibleRecords.map((record) => record.id)));
                        } else {
                          clearSelection();
                        }
                      }}
                      aria-label={
                        searchTerms.length > 0 ? 'Select all matching records' : 'Select all records'
                      }
                      className="h-3.5 w-3.5 accent-blue-600"
                    />
                  </th>
                  {canEditThisLayer && <th className="w-9 border-b border-slate-200 px-1 py-2.5" />}
                  {columns.map((column) => (
                    <th
                      key={column}
                      onContextMenu={(event) => {
                        event.preventDefault();
                        setColumnMenu({ column, point: { x: event.clientX, y: event.clientY } });
                      }}
                      title="Right-click for sort and Field Calculator"
                      className="cursor-context-menu select-none whitespace-nowrap border-b border-slate-200 px-3 py-2.5 font-semibold"
                    >
                      <span className="inline-flex items-center gap-1">
                        {column}
                        {sort?.field === column &&
                          (sort.direction === 'asc' ? (
                            <ArrowUpAZ className="h-3 w-3 text-blue-600" />
                          ) : (
                            <ArrowDownAZ className="h-3 w-3 text-blue-600" />
                          ))}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRecords.map((record) => {
                  const isSelected = selectedIds.has(record.id);
                  return (
                    <tr
                      key={record.id}
                      onClick={() => selectRecord(record)}
                      className={`cursor-pointer border-b border-slate-100 text-slate-700 ${
                        isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => selectRecord(record)}
                          onClick={(event) => event.stopPropagation()}
                          aria-label={`Select record ${record.id}`}
                          className="h-3.5 w-3.5 accent-blue-600"
                        />
                      </td>
                      {canEditThisLayer && (
                        <td className="px-1 py-2">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openEditRecord(record);
                            }}
                            title="Edit attributes"
                            aria-label={`Edit attributes for record ${record.id}`}
                            className="rounded p-1 text-slate-400 transition hover:bg-blue-50 hover:text-blue-600"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      )}
                      {columns.map((column) => {
                        const raw = record.properties[column];
                        const text =
                          raw !== null && typeof raw === 'object'
                            ? JSON.stringify(raw)
                            : String(raw ?? '—');
                        /* Marks the cell that earned the row its place. On a
                           layer with thirty columns, a matching row with no
                           visible reason is a puzzle, not an answer. */
                        return (
                          <td
                            key={column}
                            className={`whitespace-nowrap px-3 py-2 ${
                              cellMatches(text) ? 'bg-amber-100/70 font-medium text-slate-900' : ''
                            }`}
                          >
                            {text}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── All / Selected ─────────────────────────────────────────────
          A footer rather than another control in the title bar: it scopes what
          the grid above shows, so it belongs under it. */}
      {!minimised && status === 'ready' && (
        <div className="flex flex-shrink-0 items-center gap-1 border-t border-slate-200 px-2 py-1.5">
          {([
            { id: 'all' as const, label: 'All', count: records.length },
            { id: 'selected' as const, label: 'Selected', count: selectedRecords.length },
          ]).map((tab) => {
            const active = view === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setView(tab.id)}
                aria-pressed={active}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide transition ${
                  active
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-blue-600 hover:bg-blue-50'
                }`}
              >
                {tab.label}{' '}
                <span className="tabular-nums font-bold">({tab.count.toLocaleString()})</span>
              </button>
            );
          })}
          {view === 'selected' && selectedRecords.length === 0 && (
            <span className="ml-1 text-[11px] text-slate-400">
              {(preselectedKeys?.length ?? 0) > 0
                ? `${preselectedKeys!.length} selected elsewhere, but none of them are in the ${records.length.toLocaleString()} rows this window loaded.`
                : 'Nothing selected — tick a row, or run a query.'}
            </span>
          )}
        </div>
      )}

      {/* ── Docked resize divider — the one edge that DOES drag while
          docked, on the side facing the map, so a docked pane can be made
          bigger or smaller the same way any other side panel can. ────── */}
      {docked && onDockSizeChange && (
        <div
          onPointerDown={beginDockResize}
          role="separator"
          aria-orientation={dock === 'right' ? 'vertical' : 'horizontal'}
          aria-label={dock === 'right' ? 'Resize docked panel width' : 'Resize docked panel height'}
          aria-valuenow={Math.round(dockSize * 100)}
          className={
            dock === 'right'
              ? 'absolute inset-y-0 left-0 z-10 w-1.5 -translate-x-1/2 cursor-ew-resize'
              : 'absolute inset-x-0 top-0 z-10 h-1.5 -translate-y-1/2 cursor-ns-resize'
          }
          style={{ touchAction: 'none' }}
        />
      )}

      {/* ── Resize handles (hidden while maximised/minimised/docked — a
          docked pane's size comes from its parent, not a drag) ──────────── */}
      {!docked && !minimised && !maximised && (
        <>
          <div
            onPointerDown={(event) => beginInteraction(event, 'resize-e')}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize width"
            className="absolute inset-y-8 right-0 w-1.5 cursor-ew-resize"
            style={{ touchAction: 'none' }}
          />
          <div
            onPointerDown={(event) => beginInteraction(event, 'resize-s')}
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize height"
            className="absolute inset-x-8 bottom-0 h-1.5 cursor-ns-resize"
            style={{ touchAction: 'none' }}
          />
          <div
            onPointerDown={(event) => beginInteraction(event, 'resize-se')}
            role="separator"
            aria-label="Resize attribute table"
            className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize"
            style={{ touchAction: 'none' }}
          >
            <span className="absolute bottom-1 right-1 h-2 w-2 rounded-sm border-b-2 border-r-2 border-slate-400" />
          </div>
        </>
      )}
    </div>

    {/* ── Edit-attributes popup ─────────────────────────────────────────
        A separate modal rather than an inline row-expansion: the same
        field-type-aware form the Edit tool's own attribute editor uses
        (via featureEditing.ts), so a value typed here and one typed there
        are validated and saved identically. */}
    {editingRecord && editInfo && (
      <div
        className="gl-modal-backdrop fixed inset-0 z-[4000] flex items-center justify-center p-4"
        onPointerDown={(event) => event.stopPropagation()}
        onClick={() => !editSaving && setEditingRecord(null)}
      >
        <div
          role="dialog"
          aria-label="Edit attributes"
          onClick={(event) => event.stopPropagation()}
          className="gl-modal flex max-h-[80vh] w-full max-w-sm flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <h3 className="text-[13px] font-semibold text-slate-800">Edit attributes</h3>
            <button
              type="button"
              onClick={() => setEditingRecord(null)}
              aria-label="Close"
              className="rounded p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {editInfo.fields.length === 0 && (
              <p className="text-[12px] text-slate-500">This layer has no editable fields.</p>
            )}
            {editInfo.fields.map((field) => (
              <EditFieldInput
                key={field.name}
                field={field}
                value={editDraft[field.name]}
                onChange={(value) => setEditDraft((current) => ({ ...current, [field.name]: value }))}
              />
            ))}
            {editError && <p className="text-[12px] font-medium text-red-600">{editError}</p>}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-3">
            <button
              type="button"
              onClick={() => setEditingRecord(null)}
              disabled={editSaving}
              className="gl-btn gl-btn--ghost px-3 py-1.5 text-[12.5px]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void saveEditRecord()}
              disabled={editSaving || editInfo.fields.length === 0}
              className="gl-btn gl-btn--primary px-3 py-1.5 text-[12.5px]"
            >
              {editSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {editSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Add-field popup ──────────────────────────────────────────────── */}
    {addFieldOpen && (
      <div
        className="gl-modal-backdrop fixed inset-0 z-[4000] flex items-center justify-center p-4"
        onPointerDown={(event) => event.stopPropagation()}
        onClick={() => !addFieldSaving && setAddFieldOpen(false)}
      >
        <div
          role="dialog"
          aria-label="Add field"
          onClick={(event) => event.stopPropagation()}
          className="gl-modal w-full max-w-sm overflow-hidden"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <h3 className="text-[13px] font-semibold text-slate-800">Add field</h3>
            <button
              type="button"
              onClick={() => setAddFieldOpen(false)}
              aria-label="Close"
              className="rounded p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-3 px-4 py-3">
            <label className="block space-y-1">
              <span className="text-[11px] font-medium text-slate-600">Field name</span>
              <input
                type="text"
                value={newFieldName}
                onChange={(event) => setNewFieldName(event.target.value)}
                placeholder="e.g. inspection_date"
                className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12.5px]"
                autoFocus
              />
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-medium text-slate-600">Display label (optional)</span>
              <input
                type="text"
                value={newFieldAlias}
                onChange={(event) => setNewFieldAlias(event.target.value)}
                placeholder="e.g. Inspection Date"
                className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12.5px]"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-medium text-slate-600">Field type</span>
              <select
                value={newFieldType}
                onChange={(event) => setNewFieldType(event.target.value as NewFieldDef['type'])}
                className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12.5px]"
              >
                <option value="string">Text</option>
                <option value="number">Number</option>
                <option value="boolean">Yes / No</option>
              </select>
            </label>
            {addFieldError && <p className="text-[12px] font-medium text-red-600">{addFieldError}</p>}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-3">
            <button
              type="button"
              onClick={() => setAddFieldOpen(false)}
              disabled={addFieldSaving}
              className="gl-btn gl-btn--ghost px-3 py-1.5 text-[12.5px]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submitAddField()}
              disabled={addFieldSaving}
              className="gl-btn gl-btn--primary px-3 py-1.5 text-[12.5px]"
            >
              {addFieldSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {addFieldSaving ? 'Adding…' : 'Add field'}
            </button>
          </div>
        </div>
      </div>
    )}

    {columnMenu && (
      <LayerOptionsMenu
        ariaLabel={`${columnMenu.column} column options`}
        point={columnMenu.point}
        onClose={() => setColumnMenu(null)}
        items={[
          {
            id: 'sort-asc',
            label: 'Sort Ascending',
            icon: ArrowUpAZ,
            onSelect: () => setSort({ field: columnMenu.column, direction: 'asc' }),
          },
          {
            id: 'sort-desc',
            label: 'Sort Descending',
            icon: ArrowDownAZ,
            onSelect: () => setSort({ field: columnMenu.column, direction: 'desc' }),
          },
          ...(sort?.field === columnMenu.column
            ? ([
                {
                  id: 'clear-sort',
                  label: 'Clear Sort',
                  icon: X,
                  onSelect: () => setSort(null),
                },
              ] satisfies LayerMenuItem[])
            : []),
          ...(canEditThisLayer
            ? ([
                {
                  id: 'field-calculator',
                  label: 'Field Calculator…',
                  icon: Calculator,
                  separatorBefore: true,
                  disabled: !editInfo?.fields.some((field) => field.name === columnMenu.column),
                  tooltip: !editInfo?.fields.some((field) => field.name === columnMenu.column)
                    ? 'This field cannot be edited.'
                    : undefined,
                  onSelect: () => {
                    setCalculatorField(columnMenu.column);
                    setCalculatorOpen(true);
                  },
                },
              ] satisfies LayerMenuItem[])
            : []),
        ]}
      />
    )}

    {calculatorOpen && editInfo && (
      <FieldCalculatorDialog
        layer={layer}
        fields={editInfo.fields}
        objectIdField={editInfo.objectIdField}
        records={records}
        selectedIds={selectedIds}
        initialField={calculatorField}
        moduleId={moduleId}
        arcgisUrl={arcgisUrl}
        onClose={() => setCalculatorOpen(false)}
        onFieldAddedRemotely={() => setSchemaRefreshToken((token) => token + 1)}
        onApplied={(updatedLayer, targetFieldName, writes) => {
          if (updatedLayer) onLayerChange?.(layer.id, updatedLayer);
          if (writes.length === 0) return;
          // Reflected immediately for every layer type, the same reason the
          // single-row edit's own saveEditRecord updates `records` directly
          // rather than waiting for the load effect to re-run: an ArcGIS
          // layer's bulk write has no `updatedLayer` to hand back at all
          // (the service IS the source of truth), so without this an
          // ArcGIS Field Calculator run would look like it did nothing
          // until the window was closed and reopened.
          const byId = new Map(writes.map((write) => [write.id, write.value]));
          setRecords((current) =>
            current.map((row) => {
              if (!byId.has(row.id)) return row;
              const value = byId.get(row.id);
              return {
                ...row,
                [targetFieldName]: value,
                properties: { ...row.properties, [targetFieldName]: value },
              };
            }),
          );
        }}
      />
    )}
    </>
  );
}
